import http from "./http";
import environments from "../environment";

// ── Types ─────────────────────────────────────────────────────────────────────

export type AuditEvent = {
	id: string;
	occurred_at: string;
	app: string;
	category: string;
	action: string;
	actor_email: string | null;
	target_type: string | null;
	target_id: string | null;
	summary: string | null;
	severity: number;
	req_id: string | null;
	diff?: { field: string; before: any; after: any }[];
};

export type ListEventsParams = {
	actor_email?: string;
	target_type?: string;
	action?: string;
	app?: string;
	severity_min?: number;
	since?: string;
	until?: string;
	cursor?: string;
	limit?: number;
};

export type ListEventsResult = {
	data: AuditEvent[];
	nextCursor: string | null;
};

export type AuditAdminUser = {
	id: number;
	fullName: string | null;
	email: string;
	isSuperAdmin: boolean;
};

// ── Event endpoints ───────────────────────────────────────────────────────────

export async function listAuditEvents(params: ListEventsParams = {}): Promise<ListEventsResult> {
	const { data } = await http.get("/audit/admin/events", { params });
	return {
		data: (data?.data || []) as AuditEvent[],
		nextCursor: data?.nextCursor ?? null,
	};
}

export async function getAuditEventDetail(id: string): Promise<AuditEvent | null> {
	try {
		const { data } = await http.get(`/audit/admin/events/${id}`);
		return (data?.data ?? null) as AuditEvent | null;
	} catch {
		return null;
	}
}

export async function getRelatedAuditEvents(id: string): Promise<AuditEvent[]> {
	try {
		const { data } = await http.get(`/audit/admin/events/${id}/related`);
		return (data?.data || []) as AuditEvent[];
	} catch {
		return [];
	}
}

export async function countAuditEventsSince(since: string): Promise<number> {
	const { data } = await http.get("/audit/admin/events/count", { params: { since } });
	return data?.count ?? 0;
}

/** Returns a URL string that the browser can use to trigger a CSV download. */
export function buildAuditExportUrl(params: ListEventsParams = {}): string {
	const baseUrl = environments.apiRoot || environments.localApiRoot || "";
	const query = new URLSearchParams();
	Object.entries(params).forEach(([k, v]) => {
		if (v != null && v !== "") query.set(k, String(v));
	});
	const qs = query.toString();
	return `${baseUrl}/audit/admin/export${qs ? "?" + qs : ""}`;
}

// ── Archive Search endpoints ──────────────────────────────────────────────────

export type ArchiveSearchParams = {
	since: string;
	until: string;
	actor_email?: string;
	action?: string;
	app?: string;
	severity_min?: number;
};

export type ArchiveJobStatus = "pending" | "running" | "done" | "failed";

export type ArchiveSearchJob = {
	jobId: string;
	status: ArchiveJobStatus;
	progress: number;
	totalObjects: number | null;
	processedObjects: number;
	results: AuditEvent[] | null;
	errorMessage: string | null;
};

/**
 * Kick an archive search job. Returns the jobId to poll.
 */
export async function archiveSearchKick(params: ArchiveSearchParams): Promise<{ jobId: string }> {
	const { data } = await http.post("/audit/admin/archive-search", params);
	return { jobId: data?.jobId as string };
}

/**
 * Poll the status of an archive search job.
 */
export async function archiveSearchPoll(jobId: string): Promise<ArchiveSearchJob> {
	const { data } = await http.get(`/audit/admin/archive-search/${jobId}`);
	return {
		jobId: data?.jobId ?? jobId,
		status: data?.status ?? "pending",
		progress: data?.progress ?? 0,
		totalObjects: data?.totalObjects ?? null,
		processedObjects: data?.processedObjects ?? 0,
		results: (data?.results ?? null) as AuditEvent[] | null,
		errorMessage: data?.errorMessage ?? null,
	};
}

// ── Super-admin management endpoints ─────────────────────────────────────────

export async function listSuperAdmins(): Promise<AuditAdminUser[]> {
	const { data } = await http.get("/audit/admin/super-admins");
	return (data?.data || []) as AuditAdminUser[];
}

export async function promoteSuperAdmin(userId: number): Promise<AuditAdminUser> {
	const { data } = await http.post("/audit/admin/super-admins/promote", { user_id: userId });
	return data?.data as AuditAdminUser;
}

export async function demoteSuperAdmin(userId: number): Promise<AuditAdminUser> {
	const { data } = await http.post("/audit/admin/super-admins/demote", { user_id: userId });
	return data?.data as AuditAdminUser;
}
